import type { AppDispatch } from '../store'
import { canUseConsentPurposeOnClient } from '../consent/waitingForConsent'
import { SOURCE_TYPES } from 'constants/source'
import {
  ADD_OFFER_LIST_EXTRA,
  ADD_SPONSORED_OFFER,
  API_CALL,
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  END_OFFER_LIST_STREAM,
  OFFER_SET_SEARCH_RESULT_METADATA,
  OFFER_SET_TOUR_SEARCH_RESULT_METADATA,
  OFFER_SHARE_COMPLETE,
  OFFER_SHARE_START,
  OFFER_UPDATE_AVAILABLE_RATES,
  OFFER_UPDATE_PRICING_DATA,
  OFFER_VIEWED,
  OFFERS_CLEAR,
  REMOVE_OFFER,
  RESET_OFFER_LIST_STREAM_FOR_REFRESH,
  SET_META_SEARCH_STREAM_STATE,
  SET_OFFER_LIST_BOUNDS,
  SET_OFFER_LIST_DESCRIPTION_LOADING,
  SET_OFFER_LIST_ERROR,
  SET_OFFER_LIST_SELECTED_FILTERS,
  SET_OFFER_LIST_SOCKET_STATUS,
  UPDATE_OFFER_METADATA_DESCRIPTION,
} from './actionConstants'
import {
  FETCH_ALTERNATIVE_OFFERS,
  FETCH_AVAILABILITY_RATES_FOR_OFFER,
  FETCH_BEDBANK_OFFER_WITH_RATES,
  FETCH_BEST_OFFER_FOR_PROPERTY,
  FETCH_BEST_PRICE_FOR_OFFER,
  FETCH_OFFER,
  FETCH_LISTING_OFFERS,
  FETCH_OFFER_FLIGHT_PRICE,
  FETCH_OFFER_LIST,
  FETCH_OFFER_LIST_FILTERS,
  FETCH_OFFER_LOCATION_BREADCRUMBS,
  FETCH_OFFER_META_DATA,
  FETCH_OFFER_PROPERTY_SURROUNDINGS,
  FETCH_OFFER_TAGS,
  FETCH_LERE_SURCHARGE_TOKEN,
  FETCH_OFFERS,
  FETCH_PACKAGE_RECOMMENDATIONS,
  FETCH_POPULAR_FILTERS,
  FETCH_PROPERTY_OFFER_MAPPING,
  FETCH_PROPERTY_TAGS,
  FETCH_PROPERTY_NEARBY,
  FETCH_RELATED_TRAVEL_ITEMS,
  FETCH_SURCHARGE_MARGIN,
  FETCH_TOUR_SEARCH_FACETS,
  FETCH_TRADER_INFORMATION,
} from './apiActionConstants'

import type { SearchResultEntry, SeparatorResponse, RankingPreferences, definitions as searchDefinitions } from '@luxuryescapes/contract-search'
import type {
  EndResponse,
  ErrorResponse,
  FilterResponse,
  OfferDescriptionLoadingResponse,
  OfferDescriptionResponse,
  OfferResponse,
  SearchItemResponse as ContractSearchItemResponse,
  SearchLocationResponse,
  SelectedFiltersResponse,
} from '@luxuryescapes/contract-search/types/sockets'
import { SHOULD_AUTOBATCH } from '@reduxjs/toolkit'
import { getDomainUserId } from 'analytics/snowplow/helpers/domainUserId'
import { getBedbankOfferWithRates, getRatesForBedbankOffer } from 'api/bedbankRates'
import { getGhaPriceMatchForProperty } from 'selectors/ghaPriceMatchSelectors'
import * as CalendarV2Service from 'api/calendarV2'
import { mapSearchResultToOfferListMetaData } from 'api/mappers/hotelOfferMap'
import * as OfferService from 'api/offer'
import { streamRequest } from 'api/offer'
import * as RecommendationService from 'api/recommendations'
import { occupancyToStr } from 'api/recommendations'
import { batchFetchSurchargeMargin, createLereSurchargeTokenKey, createLereSurchargeTokens } from 'api/reservation'
import { getCheckoutCurrency } from 'checkout/selectors/payment/paymentType'
import * as SearchService from 'api/search'
import { getTagsByOfferId } from 'api/tags'
import * as TripPlannerService from 'tripPlanner/api'
import moment from 'moment'
import { buildOfferTagsCacheKey } from 'lib/experiences/experienceUtils'
import type { OFFER_TYPE_BED_BANK } from 'constants/offer'
import {
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_HOTEL_SLUG,
  OFFER_TYPE_TOUR,
  OFFER_TYPE_TOUR_SLUG,
  OFFER_TYPE_TOUR_V2,
  PRODUCT_TYPE_ULTRALUX_SLUG,
} from 'constants/offer'
import placesTree from 'constants/placesTree'
import { arrayToObject, groupBy, skip, sortBy, take, unique, uniqueBy } from 'lib/array/arrayUtils'
import { timeNowInSecond } from 'lib/datetime/time'
import getObjectKey from 'lib/object/getObjectKey'
import { buildAvailableRateKey } from 'lib/offer/availabilityUtils'
import { getCachedLereLabDistinctId } from 'lib/lab/labDistinctId'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import {
  buildLereDynamicPricingSurchargeTokenPayloads,
  buildLereSurchargeTokenPayloadsFromCartItems,
  getLereDynamicPricingGroup,
} from 'checkout/lib/utils/lereDynamicPricing'
import getHotelItemViews from 'checkout/selectors/view/itemViews/getHotelItemViews'
import { getLeHotelItems } from 'checkout/selectors/view/hotels'
import { evaluateLereSearchPricingForSearchResults, clearLereSearchPricingTrigger, hasEligibleSurchargedSearchResultInState, isPendingLereSearchPricingDecision } from 'lib/lere/maybeTriggerLereSearchPricing'
import { getEligibleRoomRateIds } from 'lib/lere/eligibleRoomRates'
import { mapPaymentOptionLabel } from 'lib/offer/filtersUtils'
import { getOfferListKeyForUser } from 'lib/offer/offerListKey'
import { getRecommendationKey } from 'lib/offer/getRecommendationKey'
import { isValidOccupancy } from 'lib/offer/occupancyUtils'
import { isBedbankOffer, isBundleOffer, isCruiseOffer, isLEOffer, isTourV1Offer } from 'lib/offer/offerTypes'
import {
  addSearchSocketListener,
  addStateListener,
  getStateListeners, removeAllSearchSocketListenersAndSafelyDisconnect,
  removeStateListenerAndMaybePause,
} from 'lib/search/searchSocketListeners'
import {
  buildDestinationSearchParamsKey,
  buildSearchParamsFromFilters,
  buildSearchParamsKey,
  buildSuggestedDatesParamsKey,
  getOccupancyFromSearchStrings,
  getShouldUseSearchPrices,
} from 'lib/search/searchUtils'
import { serialiseOccupancy } from 'lib/search/occupancy'
import { nameToSearchFriendlyQueryParamsTransform, urlTransform } from 'lib/string/removeSpaceUtils'
import uuidV4 from 'lib/string/uuidV4Utils'
import { shouldUseExperienceSearchStreaming } from 'lib/experiences/experienceSearchStreaming'
import { getCanViewLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { getMetaOfferIds } from 'marketing/api'
import { checkBypassMode } from 'marketing/lib/lib'
import { emitSocketMessage, clearLastMessage } from 'lib/search/socketEmitter'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import productLabels from 'i18n/productLabels'
import { selectRecDomainUserId } from 'selectors/accountSelectors'
import { getChannelMarkupSource } from 'selectors/channelMarkupSelector'
import type { ChannelMarkupSource } from 'lib/channelMarkup/channelMarkupV2'
import { getBedbankOfferChannelMarkupLeadSell } from 'lib/channelMarkup/channelMarkupLeadPricing'
import { isSpoofed } from 'selectors/isSpoofed'
import { getDefaultAirportCode } from 'selectors/flightsSelectors'
import { getOfferDeepLink } from 'selectors/offerDeepLinkSelector'
import { getSource, ghaPricedRequest, isPropertyCollectEnabled } from 'selectors/offerSelectors'
import invariant from 'tiny-invariant'
import { convertBedbankOfferToSummary } from './BedbankOfferActions'
import type { AppAction } from './types/ActionTypes'
import config from 'constants/config'
import { setExperienceOffersMetadata } from './ExperienceActions'

export interface PricingData {
  price: number
  value: number
  taxesAndFees: number
  propertyFees: number
  memberPrice?: number
}

export function offerShareButtonClick(pageId: string, pageType: string): AppAction {
  return {
    type: OFFER_SHARE_START,
    data: {
      pageId,
      pageType,
    },
  }
}

export function offerShareComplete(pageId: string, pageType: string, shareMethod: string): AppAction {
  return {
    type: OFFER_SHARE_COMPLETE,
    data: {
      pageId,
      pageType,
      shareMethod,
    },
  }
}

export function fetchListingOffersById(offerIds: Array<string>): AppAction {
  return dispatch => {
    unique(offerIds).forEach(id => dispatch(fetchAccumulatedListingOfferById(id)))
  }
}

export function fetchAccumulatedListingOfferById(offerId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    if (state.offer.listingOffersLoading[offerId] || state.offer.listingOfferErrors[offerId] ||
      state.offer.listingOffers?.[offerId] || state.offer.offers[offerId]) return

    dispatch({
      type: API_CALL,
      api: FETCH_LISTING_OFFERS,
      meta: {
        [SHOULD_AUTOBATCH]: true,
      },
      offerIds: [offerId],
      request: async() => [await OfferService.getAccumulatedListingOfferById(
        offerId,
        state.geo.currentRegionCode,
        { flightOrigin: getDefaultAirportCode(state), currentCurrency: state.geo.currentCurrency, isSpoofed: state.auth.account.isSpoofed },
        getChannelMarkupSource(state),
      )],
    })
  }
}

export function fetchAccumulatedOfferById(
  offerId: string,
  regionCode?: string,
  flightOrigin?: string,
  bypassRegionFilter?: boolean,
): AppAction {
  return (dispatch, getState) => {
    const state: App.State = getState()
    const alreadyLoading = state.offer.offersLoading[offerId]
    const alreadyLoaded = state.offer.offers[offerId]
    const alreadyLoadedWithBypass = state.offer.offersBypassRegion?.[offerId]
    const hadError = state.offer.offerErrors[offerId]
    // Allow re-fetch when bypass is requested but the cached offer was fetched without bypass.
    // A prior error always bails out (including the bypass case) to avoid an infinite re-fetch loop.
    if (alreadyLoading || hadError || (alreadyLoaded && (!bypassRegionFilter || alreadyLoadedWithBypass))) {
      return
    }
    const markupSource = getChannelMarkupSource(state)
    const offerDeepLink = getOfferDeepLink(state)

    const isSpoofed = state?.auth?.account.isSpoofed

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER,
      meta: {
        [SHOULD_AUTOBATCH]: true,
      },
      request: async() => {
        const newOfferId = await OfferService.getOfferIdBySlug(offerId).catch(() => undefined)
        return OfferService.getAccumulatedOfferById(
          newOfferId ?? offerId,
          regionCode || state.geo.currentRegionCode,
          flightOrigin || getDefaultAirportCode(state),
          state.geo.currentCurrency,
          isSpoofed,
          markupSource,
          offerDeepLink,
          bypassRegionFilter,
        )
      },
      offerId,
      bypassRegionFilter,
    })
  }
}

interface FetchOneOfferOptions { regionCode?: string, flightOrigin?: string, allPackages?: boolean, bypassRegionFilter?: boolean, privateRequestKey?: string }

export function fetchOfferById(offerId: string, {
  regionCode,
  flightOrigin,
  allPackages = false,
  bypassRegionFilter = false,
  privateRequestKey,
}: FetchOneOfferOptions = {}): AppAction {
  return (dispatch, getState) => {
    const state: App.State = getState()
    const isSpoofed = state.auth.account.isSpoofed

    const offerKey = privateRequestKey ? `${offerId}-${privateRequestKey}` : offerId
    const alreadyLoadedWithBypass = state.offer.offersBypassRegion?.[offerKey]

    // Same keying as fetchAccumulatedOfferById: key by offerId and use offersBypassRegion to
    // decide re-fetch, so bypass and non-bypass fetches dedupe instead of hitting separate keys.
    if (state.offer.offersLoading[offerKey] || state.offer.offerErrors[offerKey] || (state.offer.offers[offerKey] && (!bypassRegionFilter || alreadyLoadedWithBypass))) {
      return
    }
    const markupSource = getChannelMarkupSource(state)

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER,
      meta: {
        [SHOULD_AUTOBATCH]: true,
      },
      request: async() => {
        const newOfferId = await OfferService.getOfferIdBySlug(offerId).catch(() => undefined)

        return OfferService.getOfferById(newOfferId ?? offerId, {
          region: regionCode || state.geo.currentRegionCode,
          flightOrigin: flightOrigin || getDefaultAirportCode(state),
          allPackages,
          bypassRegionFilter,
          privateRequestKey,
          currentCurrency: state.geo.currentCurrency,
          isSpoofed,
          markupSource,
          offerDeepLink: getOfferDeepLink(state),
        })
      },
      offerId: offerKey,
      bypassRegionFilter,
    })
  }
}

export function fetchOfferFlightPrice(offerId: string, flightOrigin: string): AppAction {
  return (dispatch, getState) => {
    const state: App.State = getState()

    const existingOffer = state.offer.offers[offerId]
    if (existingOffer?.flightPrices[flightOrigin] || !existingOffer) {
      // already have price, no need to re-load
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER_FLIGHT_PRICE,
      request: () => OfferService.getOfferFlightPrice({
        flightOrigin,
        flightDestination: existingOffer.flightDestinationPort,
        currency: state.geo.currentCurrency,
        region: state.geo.currentRegionCode,
        duration: existingOffer.lowestPricePackage?.duration ?? 1,
        forceBundleId: existingOffer.forceBundleId,
        travelToDate: existingOffer.travelToDate,
      }),
      offerId,
      flightOrigin,
    })
  }
}

function convertLEOfferToSummary(offer: App.Offer | App.OfferSummary): App.OfferSummary {
  const {
    packages,
    ...offerSummary
  } = offer
  return {
    ...offerSummary,
    // offer summaries have the field present, so it can work in tandem with an actual offer
    // but the packages will always be empty to save space
    packages: [],
  }
}

export function convertOfferToSummary(
  offer: App.AnyOffer,
): App.AnyOffer {
  if (isBedbankOffer(offer)) {
    return convertBedbankOfferToSummary(offer)
  } else if (isBundleOffer(offer) || isCruiseOffer(offer)) {
    // some offer type doesn't know how to summary yet
    return offer
  } else if (isLEOffer(offer)) {
    return convertLEOfferToSummary(offer)
  }

  return offer
}

type FetchOfferOptions = {
  regionCode?: string
  includePackages?: boolean
  bypassRegionFilter?: boolean
  /** Ask again for ids whose last fetch failed. Loaded and in-flight ids are still skipped. */
  retryFailed?: boolean
}

/**
 * Bulk fetch of offers
 *
 * @remarks
 * Before requesting the fetch,
 * Checks are made against offers currently being loaded and offers that are missing data
 *
 * This ensures we only fetch when absolutely necessary
 */
export function fetchOffersById(
  offerIds: Array<string> = [],
  options: FetchOfferOptions = {},
): AppAction {
  return (dispatch, getState) => {
    const state: App.State = getState()
    const { includePackages, regionCode } = options
    const missingOfferIds = unique(offerIds.filter(function(id) {
      const isLoaded = !!state.offer.offers[id]
      const isLoading = !!state.offer.offersLoading[id]
      const hasError = !!state.offer.offerErrors[id]
      if (
        (!isLoaded && !isLoading && (!hasError || options.retryFailed)) ||
        (includePackages && state.offer.offers[id]?.packages.length === 0)
      ) {
        return true
      }
    }))

    if (missingOfferIds.length === 0) {
      // already have all the offers
      return
    }

    const authState: App.AuthState = state.auth
    const markupSource = getChannelMarkupSource(state)
    const offerDeepLink = getOfferDeepLink(state)

    const fetchOptions = {
      ...options,
      flightOrigin: getDefaultAirportCode(state),
      currentCurrency: state.geo.currentCurrency,
      isSpoofed: authState.account.isSpoofed,
      enablePropertyCollect: isPropertyCollectEnabled(state),
    }

    dispatch({
      type: API_CALL,
      api: FETCH_OFFERS,
      request: () => OfferService.getOffersById(missingOfferIds, regionCode || state.geo.currentRegionCode, fetchOptions, markupSource, offerDeepLink),
      offerIds: missingOfferIds,
    })
  }
}

interface Options {
  clientCheckAvailability?: boolean
  typeaheadId?: string
  functionScoreWeights?: RankingPreferences
  forceRestart?: boolean
  preserveOfferIds?: boolean
  isLerePricingRefresh?: boolean
  lereGroupOverride?: string
  noCache?: boolean
}

export function fetchOfferList(filters: App.OfferListFilters = {}, options: Options = {}): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)
    const luxPlusEnabled = getIsLuxPlusEnabled(state)
    const markupSource = getChannelMarkupSource(state)
    const offerListKey = getOfferListKeyForUser(filters, canViewLuxPlusBenefits)
    const existingList = state.offer.offerLists[offerListKey]

    if (existingList && !existingList.error) {
      // already have the results for this set of params
      return
    }

    if (filters.checkIn && !filters.checkOut) {
      // This request is only valid if both checking and checkout dates are provided
      return
    }

    const searchStreaming = shouldUseExperienceSearchStreaming(state, {
      placeId: filters.placeIds?.[0],
      from: filters.checkIn,
      to: filters.checkOut,
      bounds: filters.bounds,
      limit: filters.limit,
    })

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER_LIST,
      request: () => OfferService.getOfferList({
        region: state.geo.currentRegionCode,
        filters,
        canViewLuxPlusBenefits,
        isLuxPlusEnabled: luxPlusEnabled,
        clientCheckAvailability: options.clientCheckAvailability,
        isSpoofed: isSpoofed(state),
        accessToken: state.auth.accessToken,
        markupSource,
        searchStreaming,
      }).then((results) => {
        let metaData: Array<App.OfferListMetaData> = results.metaData || []
        const offerIds: Array<string> = results.offerIds
        // Finding all bundle offers and creating a map object
        const mapBundledOffers = arrayToObject(
          metaData.filter(m => m.bundledOfferId && m.available),
          r => r.bundledOfferId!,
          r => r.offerId,
        )

        // Adding for single offers property bundleOfferId
        metaData = metaData.map(r => mapBundledOffers[r.offerId] ? {
          ...r,
          bundleOfferId: mapBundledOffers[r.offerId],
        } : r)

        if (metaData.length > 0 && (filters.landmarkId || filters.destinationId || filters.propertyId || filters.bounds)) {
          dispatch(setSearchResultMetadata(metaData, filters, offerListKey))
        }

        const tourMetadata = results.tourMetadata || []
        if ((tourMetadata.length > 0) && (filters.destinationId)) {
          dispatch(setTourSearchResultMetadata(tourMetadata, offerListKey))
        }

        if (results.experienceOffersMetadata) {
          dispatch(setExperienceOffersMetadata(offerListKey, results.experienceOffersMetadata))
        }

        if (results.filters) {
          const offerListFilters = getOfferListFilters({
            filters: results.filters,
            filterOrder: results.filterOrder ?? { amenities: {}, holidayTypes: {}, propertyTypes: {} },
            offerCount: results.offerCount ?? 0,
          })
          dispatch({
            type: API_CALL_SUCCESS,
            api: FETCH_OFFER_LIST_FILTERS,
            data: offerListFilters,
            key: offerListKey,
          })
        }

        return {
          searchId: results.searchId,
          mainOfferList: unique(filters.limit ? take(offerIds, filters.limit) : offerIds),
          offerCount: results.offerCount,
          searchVertical: results.searchVertical,
        }
      }),
      key: offerListKey,
    })
  }
}

export function fetchPersonalisedOfferList(filters: App.OfferListFilters): AppAction {
  return (dispatch, getState) => {
    if (!canUseConsentPurposeOnClient('personalisation')) {
      return
    }

    const state = getState()
    const isLuxPlus = filters.isLuxPlus ?? getCanViewLuxPlusBenefits(state)
    const offerListKey = getOfferListKeyForUser(filters, isLuxPlus)
    const existingList = state.offer.offerLists[offerListKey]

    // Skip completed and in-flight lists for the same key. SSR soft-timeout does not leave a
    // fetching stub (apiMiddleware skips API_CALL_REQUEST when IS_SSR), so the client can still
    // dispatch after a timed-out empty hydrate. Retaining the fetching guard avoids concurrent
    // requests (e.g. canViewLuxPlusBenefits flip) racing without a request-generation check.
    if (existingList && !existingList.error) {
      return
    }

    // Include these fields in the filters to ensure data is only refetched when a new combination is selected, fallback to redux state if not provided
    const {
      leUserId,
      baseOfferId: offerId,
      minMpv,
      checkIn,
      checkOut,
      rooms,
      offerTypes,
      rankerEnabled,
      useClusters,
      rankerOfferIds,
    } = filters
    const region = state.geo.currentRegionCode
    const domainUserId = selectRecDomainUserId(state)

    let occupancy: string | undefined = undefined
    if (rooms) {
      occupancy = occupancyToStr(rooms)
    }

    const mapRecommendationsToMainOfferList = ({ offers }: { offers: Array<App.RecommendationOffer> | Array<App.Recommendation> }) => ({
      mainOfferList: offers.map(o => o.offerId),
      offerCount: offers.length,
    })
    const rankerCandidateOfferIds = rankerOfferIds?.split(',').filter(Boolean) ?? []
    // Work around for conditional dispatch
    let request = () => new Promise((resolve) => resolve({ offers: [] })).then(mapRecommendationsToMainOfferList)
    if (filters.lere === 'alternatives' && (domainUserId || leUserId)) {
      request = () => RecommendationService.getAlternativesRecommendations(region, isLuxPlus, leUserId, domainUserId).then(mapRecommendationsToMainOfferList)
    } else if (filters.lere === 'peopleLikeMe' && leUserId) {
      request = () => RecommendationService.getPeopleLikeMeRecommendations(leUserId, region, isLuxPlus).then(mapRecommendationsToMainOfferList)
    } else if (filters.lere === 'graph' && offerId) {
      request = () => RecommendationService.getRecommendations(offerId, region, isLuxPlus, false, checkIn, checkOut, occupancy, minMpv).then(mapRecommendationsToMainOfferList)
    } else if (filters.lere === 'lere-o2o' && offerId) {
      request = () => RecommendationService.getOfferToOfferRecommendations(offerId, region, isLuxPlus, false, checkIn, checkOut, occupancy, minMpv).then(mapRecommendationsToMainOfferList)
    } else if (filters.lere === 'graph-metasearch' && offerId) {
      request = () => RecommendationService.getRecommendations(offerId, region, isLuxPlus, false, checkIn, checkOut, occupancy, minMpv, true).then(mapRecommendationsToMainOfferList)
    } else if (filters.lere === 'twoTowers' && (offerId || leUserId || domainUserId)) {
      request = () => RecommendationService.getTwoTowersRecommendations({
        leUserId,
        domainUserId,
        offerId,
        region,
        isLuxPlusMember: isLuxPlus,
        checkIn,
        checkOut,
        occupancy,
        offerTypes,
        rankerEnabled,
        useClusters,
      }).then(mapRecommendationsToMainOfferList)
    } else if (filters.lere === 'ranker' && (leUserId || domainUserId) && rankerCandidateOfferIds.length) {
      request = () => RecommendationService.getRankerRecommendations({
        leUserId,
        domainUserId,
        region,
        offerIds: rankerCandidateOfferIds,
      }).then(mapRecommendationsToMainOfferList)
    } else if (filters.lere === 'topDeals') {
      request = () => RecommendationService.getTopDeals(region, isLuxPlus).then(({ offers }) => mapRecommendationsToMainOfferList({
        offers: offers.map(offer => ({
          offerId: offer.offer_id,
          offerType: offer.offer_type ?? 'hotel',
          score: offer.score,
        })),
      }))
    }

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER_LIST,
      request,
      key: offerListKey,
    })
  }
}

export function fetchOfferListFilters(filters: App.OfferListFilters): AppAction {
  // filters come with the offer list in search now
  return fetchOfferList(filters)
}

export function fetchTourSearchFacets(filters: App.OfferListFilters = {}): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const destinationId = filters.destinationId
    const excludeIds = filters.offerIdsToExclude?.join(',')

    const params = {
      placeId: destinationId,
      region: state.geo.currentRegionCode,
      category: filters.categories?.[0],
      luxPlusExclusive: filters.luxPlusFeatures,
      agentHubExclusive: filters.agentHubFeatures,
      priceGte: filters.priceGte ? parseFloat(filters.priceGte) : undefined,
      priceLte: filters.priceLte ? parseFloat(filters.priceLte) : undefined,
      onSale: filters.onSale,
      flashOffer: filters.flashOffer,
      tourLengthGte: filters.tourLengthGte ? parseFloat(filters.tourLengthGte) : undefined,
      tourLengthLte: filters.tourLengthLte ? parseFloat(filters.tourLengthLte) : undefined,
      tourStyle: filters.tourStyles?.[0],
    }

    const key = getObjectKey(params)
    if (state.tour.tourFacets[key] && !excludeIds) {
      // already have the results for this set of params
      return
    }
    if ((filters.offerTypes?.includes(OFFER_TYPE_TOUR) || filters.offerTypes?.includes(OFFER_TYPE_TOUR_V2)) && destinationId) {
      dispatch({
        type: API_CALL,
        api: FETCH_TOUR_SEARCH_FACETS,
        request: () => SearchService.getTourFacets({ ...params, placeId: destinationId, excludeIds }),
        key,
      })
    }
  }
}

interface BestPriceParams {
  checkIn: string
  checkOut: string
  occupants: Array<App.Occupants>
  bundledOfferId?: string
}

export function fetchBestPriceForOffer(offer: App.HotelOffer | App.HotelOfferSummary | App.VillaOffer | App.VillaOfferSummary | App.TourOffer | App.TourOfferSummary | App.BundleOffer, params: BestPriceParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const key = buildSearchParamsKey(params.checkIn, params.checkOut, params.occupants, params.bundledOfferId)
    const offerId = offer.id
    const markupSource = getChannelMarkupSource(state)

    if (
      state.offer.offerBestPrices[offerId]?.[key] ||
      state.offer.offerPricesLoading[offerId]?.[key] ||
      state.offer.offerPricesErrors[offerId]?.[key]
    ) {
      // Already have details, or are loading them, or encountered an error while loading them
      return
    }

    // occupants we're given don't transform children/infant categories
    // based on property, so we need to do it here
    const rooms = params.occupants.map(occupants =>
      getOccupancyFromSearchStrings(
        occupants.adults,
        occupants.childrenAge,
        offer.property?.maxChildAge,
        offer.property?.maxInfantAge,
        offer.property?.maxTeenagerAge,
      ),
    )

    dispatch({
      key,
      offerId,
      type: API_CALL,
      api: FETCH_BEST_PRICE_FOR_OFFER,
      request: () => CalendarV2Service.getLowestPriceForOffer({
        offerType: offer.type,
        offerIds: [offerId],
        regionCode: state.geo.currentRegionCode,
        currencyCode: state.geo.currentCurrency,
        timezone: offer.property?.timezone ?? 'UTC',
        checkIn: params.checkIn,
        checkOut: params.checkOut,
        rooms,
        bundledOfferId: params.bundledOfferId,
        saleUnit: offer.saleUnit,
        markupSource,
        channelMarkupLeadPricing: 'channelMarkupLeadPricing' in offer ? offer.channelMarkupLeadPricing : undefined,
      }),
    })
  }
}

export function fetchBestOfferForProperty(propertyId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const [propertyType, id] = propertyId.split(':')

    if (state.offer.bestPropertyOffer[propertyId]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_BEST_OFFER_FOR_PROPERTY,
      request: () => SearchService.getAlternativeOffers(
        id,
        propertyType === 'le' ? 'le' : 'bedbank',
        state.geo.currentRegionCode,
      ).then(result => {
        const leResultsByType = groupBy(result.leOfferDetails, res => res.type)

        // priority list of flash -> TAO -> LME -> bedbank
        const best = (
          leResultsByType.get(OFFER_TYPE_HOTEL) ??
          leResultsByType.get(OFFER_TYPE_ALWAYS_ON)
        )
        return {
          offerId: best?.[0].offerId ?? result.bedbank[0],
          type: best?.[0].offerId ? 'le' as const : 'bedbank' as const,
        }
      }),
      propertyId,
    })
  }
}

function filterByDefinedProperty<T, K extends keyof T>(arr: Array<T>, key: K): Array<T & { [key in K]: {} }> {
  function hasKey(el: T): el is T & { [key in K]: {} } {
    return !!el[key]
  }
  return arr.filter(hasKey)
}

export function offerListKeepsSearchMetadata(filters: App.OfferListFilters): boolean {
  return !!(
    filters.landmarkId
    || filters.destinationId
    || filters.destinationIds?.length
    || filters.propertyId
    || filters.bounds
    || (filters.lat && filters.lng)
    || filters.query
  )
}

/** Singular cache key. An existing destinationId stays ahead of destinationIds. */
export function offerListSearchTargetId(filters: App.OfferListFilters): string {
  return filters.landmarkId
    ?? filters.destinationId
    ?? (filters.destinationIds?.length ? filters.destinationIds.join(',') : undefined)
    ?? filters.propertyId
    ?? ''
}

function setSearchResultMetadata(
  metaData: Array<App.OfferListMetaData>,
  filters: App.OfferListFilters,
  offerListKey: string,
): AppAction {
  const searchTargetId = offerListSearchTargetId(filters)
  const availabilityKey = buildDestinationSearchParamsKey(searchTargetId, filters.checkIn, filters.checkOut, filters.rooms)
  const suggestedDatesKey = buildSuggestedDatesParamsKey(filters.flexibleMonths, filters.flexibleNights, filters.rooms)

  return {
    type: OFFER_SET_SEARCH_RESULT_METADATA,
    data: {
      distanceFromSearchTarget: {
        [searchTargetId]: arrayToObject(filterByDefinedProperty(metaData, 'distance'), r => r.offerId, r => r.distance),
      },
      // all offers include unavailable ones
      offerAvailabilityFromSearchTarget: {
        [availabilityKey]: arrayToObject(filterByDefinedProperty(metaData, 'available'), r => r.offerId, r => r.available),
      },
      offerSuggestedDates: {
        [suggestedDatesKey]: arrayToObject(filterByDefinedProperty(metaData, 'suggestedTravelDates'), r => r.offerId, r => r.suggestedTravelDates),
      },
      offerMetaData: {
        [offerListKey]: arrayToObject(metaData, r => r.offerId, r => r),
      },
    },
    searchTargetId,
    availabilityKey,
    suggestedDatesKey,
    offerMetaDataKey: offerListKey,
  }
}

function setTourSearchResultMetadata(
  tourMetadata: Array<App.TourListMetadata>,
  offerListKey: string,
): AppAction {
  return {
    type: OFFER_SET_TOUR_SEARCH_RESULT_METADATA,
    data: {
      tourMetadata: {
        [offerListKey]: arrayToObject(tourMetadata, r => r.tourId, r => r),
      },
    },
    tourMetadataKey: offerListKey,
  }
}

export interface AvailableRatesParams {
  checkIn: string;
  checkOut: string;
  occupants: Array<App.Occupants>;
  currencyCode?: string;
  flightOrigin?: string;
  bundleOfferId?: string;
  reservationId?: string;
  packageIds?: Array<string>;
  bypassRegionFilter?: boolean;
  lereGroup?: string;
}

export function fetchAvailableRatesForEachRoom(offer: App.Offer, params: AvailableRatesParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const offerId = offer.id
    const offerAvailableRatesByOccupancy = state.offer.offerAvailableRatesByOccupancy[offerId]
    const fetchedKeys = new Set(Object.keys(offerAvailableRatesByOccupancy || {}))

    params.occupants.map(occupants => {
      const key = buildAvailableRateKey(params.checkIn, params.checkOut, [occupants])
      if (!fetchedKeys.has(key)) {
        fetchedKeys.add(key)
        dispatch(fetchAvailableRates(offer, {
          ...params,
          occupants: [occupants],
        }))
      }
    })
  }
}

function updateAvailableRatesForOffer(offerId: string, key: string): AppAction {
  return {
    type: OFFER_UPDATE_AVAILABLE_RATES,
    offerId,
    key,
  }
}

export function fetchAvailableRatesForOffer(offer: App.Offer | App.OfferSummary | App.BundledOffer, params: AvailableRatesParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const offerId = offer.id
    const offerAvailableRates = state.offer.offerAvailableRatesByOccupancy[offerId]
    const currentOfferAvailableRates = state.offer.offerAvailableRates[offerId]
    const offerAvailableRatesLereGroups = state.offer.offerAvailableRatesLereGroups?.[offerId]

    const key = buildAvailableRateKey(params.checkIn, params.checkOut, params.occupants)
    const cachedLereGroup = offerAvailableRatesLereGroups?.[key]
    // Rate keys omit LERE group; track it separately.
    const cacheMatchesRequestedLereGroup = cachedLereGroup === params.lereGroup

    if (params.packageIds) {
      // If `offerAvailableRates[key]` does not exist, then we're performing a partial load from
      // scratch. If it _does_ exist and its `packageIds` is `undefined`, that means we've already
      // kicked off a full load.
      // Group changes invalidate loaded package ids.
      const loadedPackageIds = offerAvailableRates?.[key] && cacheMatchesRequestedLereGroup ? offerAvailableRates[key].packageIds : []
      if (loadedPackageIds == null) return

      const missingPackageIds = [...new Set(params.packageIds).difference(new Set(loadedPackageIds))]
      if (!missingPackageIds.length) return

      dispatch(fetchAvailableRates(offer, {
        ...params,
        packageIds: missingPackageIds,
      }))
    } else if (!offerAvailableRates || !(key in offerAvailableRates) || offerAvailableRates[key].packageIds || !cacheMatchesRequestedLereGroup) {
      // we have not fetched data with this key before, call the API
      dispatch(fetchAvailableRates(offer, params))
    } else if (!!currentOfferAvailableRates && !(key in currentOfferAvailableRates)) {
    // we did fetched it before but offerAvailableRates is not using data with that key
      dispatch(updateAvailableRatesForOffer(offerId, key))
    }
  }
}

function fetchAvailableRates(offer: App.Offer | App.OfferSummary | App.BundledOffer, params: AvailableRatesParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const offerId = offer.id
    const key = buildAvailableRateKey(params.checkIn, params.checkOut, params.occupants)
    const markupSource = getChannelMarkupSource(state)

    // occupants we're given don't transform children/infant categories
    // based on property, so we need to do it here
    const rooms = params.occupants.map(occupants =>
      getOccupancyFromSearchStrings(
        occupants.adults,
        occupants.childrenAge,
        offer.property?.maxChildAge,
        offer.property?.maxInfantAge,
        offer.property?.maxTeenagerAge,
      ),
    )

    dispatch({
      type: API_CALL,
      api: FETCH_AVAILABILITY_RATES_FOR_OFFER,
      request: () => CalendarV2Service.getAvailableRatesForOffer({
        offerType: offer.type,
        offerIds: [params.bundleOfferId ?? offerId],
        regionCode: state.geo.currentRegionCode,
        currencyCode: params.currencyCode || state.geo.currentCurrency,
        timezone: offer.property?.timezone ?? 'UTC',
        checkIn: params.checkIn,
        checkOut: params.checkOut,
        rooms,
        dynamic: true,
        flightOrigin: params.flightOrigin,
        bundledOfferId: params.bundleOfferId ? offerId : undefined,
        markupSource,
        channelMarkupLeadPricing: 'channelMarkupLeadPricing' in offer ? offer.channelMarkupLeadPricing : undefined,
        reservationId: params?.reservationId,
        packageIds: params?.packageIds,
        bypassRegionFilter: params?.bypassRegionFilter,
        lereGroup: params?.lereGroup,
      }),
      offerId,
      key,
      packageIds: params?.packageIds,
      lereGroup: params?.lereGroup,
    })
  }
}

function searchForLocation(location: string, place: App.Place): Array<App.Place> | undefined {
  if (place.name === location || place.canonicalName === location) {
    return [place]
  }
  if (place.children) {
    for (const child of place.children) {
      const result = searchForLocation(location, child)
      if (result !== undefined) {
        return [place, ...result]
      }
    }
  }
  return undefined
}

function getDeepestLocation(locations: Array<string>): Array<string> {
  let bestLocationPaths = [undefined] as Array<App.Place | undefined>
  for (const location of locations) {
    const locationPath = searchForLocation(location, { id: '', name: 'root', children: placesTree })
    if (locationPath !== undefined && locationPath.length > bestLocationPaths.length) {
      bestLocationPaths = locationPath
    }
  }

  return bestLocationPaths.length > 1 ? skip(bestLocationPaths, 1).map(path => path!.name) : take(locations, 1)
}

export function getRelatedTravelItemsForTour(offer: App.TourOffer): Array<App.BreadcrumbItem> {
  const intl = getCurrentIntl()
  const locations = getDeepestLocation(offer.locations)
  const breadcrumbs: Array<App.BreadcrumbItem> = [
    {
      text: intl.formatMessage(productLabels.tours),
      url: `/${OFFER_TYPE_TOUR_SLUG}`,
    },
    ...locations.map(location => ({ text: location, url: `/${urlTransform(`${location}.Tours & Cruises`)}`.toLowerCase() })),
    { text: offer.lowestPricePackage.tour.name },
  ]
  return breadcrumbs
}

function breadcrumbURLConstructor(
  destination: Array<SearchService.Destination>,
  checkIn?: string,
  checkOut?: string,
  rooms?: Array<App.Occupants>,
) {
  const roomsWithFallback = rooms ?? [config.search.defaultOccupants]
  const searchParams = buildSearchParamsFromFilters({
    checkIn,
    checkOut,
    rooms: roomsWithFallback,
  })
  return `/search?destinationName=${nameToSearchFriendlyQueryParamsTransform(destination[0].name)}&destinationId=${destination[0].place_id}&${searchParams}`
}

export async function breadcrumbsFetcher(offer: App.Offer | App.BedbankOffer, state?: App.State, checkIn?: string, checkOut?: string, rooms?: Array<App.RoomOccupants>): Promise<Array<App.BreadcrumbItem>> {
  invariant(offer.property, 'Missing property on offer ' + offer.id)
  const leOffer = isLEOffer(offer)
  const intl = getCurrentIntl()

  let firstItem = {
    text: intl.formatMessage(productLabels.hotels),
    url: `/${OFFER_TYPE_HOTEL_SLUG}`,
  }
  if (leOffer && offer.property.isUltraLux) {
    firstItem = {
      text: intl.formatMessage(productLabels.ultraLux),
      url: `/${PRODUCT_TYPE_ULTRALUX_SLUG}`,
    }
  }

  let destinations: Array<SearchService.Destination>
  if (state?.offer.relatedTravelItems[offer.id]) {
    destinations = state.offer.relatedTravelItems[offer.id].map((destination) => {
      if (destination.placeId) {
        return { name: destination.text, place_id: destination.placeId }
      }
    }).filter((breadcrumb) => breadcrumb !== undefined) as Array<SearchService.Destination>
  } else {
    destinations = await SearchService.getBreadcrumbs(offer.property.id, (leOffer ? 'le' : 'bedbank'), false)
    destinations = destinations.reverse()
  }
  const locationPathDeduplicated = uniqueBy(destinations, d => d.name + '|' + d.place_id)
  const breadcrumbs = locationPathDeduplicated
    .map((location, index) => [location, ...locationPathDeduplicated.slice(0, index).reverse()])
    .map((locationPath) => ({
      text: locationPath[0].name,
      url: breadcrumbURLConstructor(locationPath, checkIn, checkOut, rooms),
      placeId: locationPath[0].place_id,
    }))

  let lastItem: App.BreadcrumbItem
  if (leOffer) {
    lastItem = {
      text: offer.property.name,
      url: `/offer/${offer.slug}/${offer.id}`,
    }
  } else {
    lastItem = {
      text: offer.name,
      url: `/partner/${offer.slug}/${offer.id}`,
    }
  }

  return [
    firstItem,
    ...breadcrumbs,
    lastItem,
  ]
}

export function fetchRelatedTravelItems(offer: App.Offer | App.BedbankOffer, checkIn?: string, checkOut?: string, rooms?: Array<App.RoomOccupants>): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (isTourV1Offer(offer)) {
      if (state.offer.relatedTravelItems[offer.id] !== undefined) {
        return
      }
      dispatch({
        type: API_CALL_SUCCESS,
        api: FETCH_RELATED_TRAVEL_ITEMS,
        offerId: offer.id,
        data: getRelatedTravelItemsForTour(offer),
      })
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_RELATED_TRAVEL_ITEMS,
      request: () => breadcrumbsFetcher(offer, state, checkIn, checkOut, rooms),
      offerId: offer.id,
    })
  }
}

export function fetchAlternativeOffers(propertyId: string, type: 'le' | 'bedbank', additionalKey: string = ''): AppAction {
  const key = [propertyId, additionalKey].join('')
  return (dispatch, getState) => {
    const state: App.State = getState()

    dispatch({
      type: API_CALL,
      api: FETCH_ALTERNATIVE_OFFERS,
      request: () => SearchService.getAlternativeOffers(propertyId, type, state.geo.currentRegionCode),
      key,
    })
  }
}

export function clearOffers(
  ids: {
    offerIds?: Array<string>
    bedbankOfferIds?: Array<string>
    tourV2OfferIds?: Array<string>
  },
): AppAction {
  return {
    type: OFFERS_CLEAR,
    ids,
  }
}

export function fetchTraderInformation(
  offerId: string,
  regionCode?: string,
): AppAction {
  return (dispatch, getState) => {
    const state: App.State = getState()

    if (state.offer.traderInformation[offerId]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_TRADER_INFORMATION,
      request: () =>
        OfferService.getTraderInformation(
          offerId,
          regionCode || state.geo.currentRegionCode,
        ),
      offerId,
    })
  }
}

export function offerViewed(offerId: string, offerType: App.OfferType, isPriceMissing: boolean = false): AppAction {
  return (dispatch) => {
    const newView: App.RecentlyViewedOffer = {
      offerId,
      offerType,
      creationTime: timeNowInSecond(), // store view time as sec
      category: 'recently_viewed',
      lereVersion: 'recently_viewed_local',
      isPriceMissing,
    }

    dispatch({
      type: OFFER_VIEWED,
      view: newView,
    })
  }
}

export function fetchOfferLocationBreadcrumbs(propertyId: string, type: 'le' | 'bedbank', showAllLocations: boolean): AppAction {
  return (dispatch, getState) => {
    const state: App.State = getState()

    if (state.offer.locationBreadcrumbs[propertyId]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER_LOCATION_BREADCRUMBS,
      request: () => SearchService.getBreadcrumbs(propertyId, type, showAllLocations),
      propertyId,
    })
  }
}

export function updateHotelPackagePricingData(
  offerId: string,
  uniqueKey: string,
  data: Partial<PricingData>,
): AppAction {
  return {
    type: OFFER_UPDATE_PRICING_DATA,
    offerId,
    uniqueKey,
    data,
  }
}

export function fetchPopularHotelFilters(): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    dispatch({
      type: API_CALL,
      api: FETCH_POPULAR_FILTERS,
      request: () => SearchService.getPopularHotelFilters(state.geo.currentRegionCode),
    })
  }
}

function addOfferListExtra(offerListKey: string, extra: App.OfferListExtra): AppAction {
  return {
    type: ADD_OFFER_LIST_EXTRA,
    key: offerListKey,
    extra,
  }
}

function endOfferListStream(action: { offerListKey: string, searchId: string }): AppAction {
  const { offerListKey, searchId } = action

  return {
    type: END_OFFER_LIST_STREAM,
    key: offerListKey,
    searchId,
  }
}

function setMetaSearchStreamState(offerListKey: string, streamState: App.MetaSearchStreamState): AppAction {
  return {
    type: SET_META_SEARCH_STREAM_STATE,
    key: offerListKey,
    streamState,
  }
}

export type StreamingHotelSearchOffersResponse = { result: Array<SearchResultEntry>, searchId: string }

export enum SEPARATOR_TYPE {
  NEARBY_OFFERS = 'NEARBY_OFFERS',
  SECOND_TIER_AVAILABLE = 'SECOND_TIER_AVAILABLE',
}

type SearchItemResponse = ContractSearchItemResponse & {
  itemType?: App.OfferListGroupedItemType
}

interface SponsoredOfferResponse {
  searchId: string
  position: number
  result: SearchResultEntry
}

export function streamOfferList(
  filters: App.OfferListFilters = {},
  listenerId: string,
  minRequiredOffers?: number,
  newSearchId?: string,
  options: Options = {},
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const {
      clientCheckAvailability,
      typeaheadId,
      functionScoreWeights,
      forceRestart = false,
      preserveOfferIds = false,
      isLerePricingRefresh = false,
      lereGroupOverride,
      noCache,
    } = options
    if (filters.rooms && !isValidOccupancy(...filters.rooms)) return

    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)
    const luxPlusEnabled = getIsLuxPlusEnabled(state)
    const domainUserId = getDomainUserId()
    const memberId = state.auth.account.memberId
    const personalise = filters.shouldUsePersonalizedSearch
    const lereGroup = lereGroupOverride ?? getLereDynamicPricingGroup(state)
    const streamFilters = lereGroup ? { ...filters, lereGroup } : filters
    // LERE group adjusts surcharges via svc-reservation; skip deferred date checks when set.
    const streamClientCheckAvailability = lereGroup ? false : clientCheckAvailability
    const markupSource = getChannelMarkupSource(state)
    // `lowestPrice` covers the whole party, so a `fixed` markup is marked once per room against it.
    // Absent rooms is a request with no occupancy (an undated browse), which is a single room.
    const searchRoomCount = filters.rooms?.length ?? 1

    const offerListKey = getOfferListKeyForUser(filters, canViewLuxPlusBenefits)
    const existingOfferList = state.offer.offerLists[offerListKey]

    if (!isLerePricingRefresh && !existingOfferList) {
      clearLereSearchPricingTrigger(offerListKey)
    }

    addStateListener({ eventGroupKey: offerListKey, listenerId })

    if (forceRestart && existingOfferList) {
      if (existingOfferList.searchId) {
        clearLastMessage(existingOfferList.searchId)
      }
      removeAllSearchSocketListenersAndSafelyDisconnect({ eventGroupKey: offerListKey })
      dispatch({
        type: RESET_OFFER_LIST_STREAM_FOR_REFRESH,
        key: offerListKey,
        isLerePricingRefresh,
      })
    } else if (existingOfferList?.streamEnded ||
      existingOfferList?.socketListenersStatus === 'listening'
    ) {
      if (existingOfferList.streamEnded && !isLerePricingRefresh) {
        evaluateLereSearchPricingForSearchResults({
          dispatch,
          getState,
          filters,
          isLerePricingRefresh,
        }).then(({ assignedLereGroup }) => {
          if (assignedLereGroup) {
            dispatch(refreshOfferListWithLereGroup(filters, listenerId, assignedLereGroup, minRequiredOffers))
          }
        }).catch(() => {})
      }
      // already have the results for this set of params
      return
    }

    if (filters.checkIn && !filters.checkOut) {
      // This request is only valid if both checking and checkout dates are provided
      return
    }

    const searchId = forceRestart
      ? (newSearchId ?? uuidV4())
      : (existingOfferList?.searchId ?? newSearchId ?? uuidV4())
    const shouldPreserveOfferIds = preserveOfferIds || forceRestart

    // We only want to dispatch API_CALL_REQUEST when we are making a request for the first time
    if (!existingOfferList || forceRestart) {
      dispatch({
        type: API_CALL_REQUEST,
        api: FETCH_OFFER_LIST,
        key: offerListKey,
        searchId,
        preserveOfferIds: shouldPreserveOfferIds,
        isLerePricingRefresh,
      })
    }

    if (!isLerePricingRefresh && isPendingLereSearchPricingDecision(state, filters) && filters.checkIn && filters.checkOut) {
      getEligibleRoomRateIds(filters.checkIn, filters.checkOut).catch(() => {})
    }

    dispatch({
      type: SET_OFFER_LIST_SOCKET_STATUS,
      key: offerListKey,
      status: 'listening',
    })

    const handleFiltersResponse = (response: FilterResponse) => {
      if (response.searchId !== searchId) return

      const offerListFilterResults = mapOfferListFiltersResponse(response)

      dispatch({
        type: API_CALL_SUCCESS,
        api: FETCH_OFFER_LIST_FILTERS,
        data: {
          ...getOfferListFilters(offerListFilterResults),
          offerCount: offerListFilterResults.offerCount,
        },
        key: offerListKey,
      })
    }

    // Batch streamed offer dispatches to avoid "Maximum update depth exceeded".
    const STREAM_DISPATCH_BUFFER_MS = 16
    let bufferedStreamedOfferIds: Array<string> = []
    let bufferedStreamedMapOfferIds: Array<string> = []
    let bufferedOfferMetaData: Array<App.OfferListMetaData> = []
    let flushBufferedOffersTimeout: ReturnType<typeof setTimeout> | undefined

    // Hold streamed offers while /decide runs once a surcharged room rate appears; metadata still flushes for eligibility.
    let isHoldingOffersForLereDecision = false
    let heldOfferIds: Array<string> = []
    let heldMapOfferIds: Array<string> = []

    const isCurrentStream = () => getState().offer.offerLists[offerListKey]?.searchId === searchId

    const maybeStartHoldingForLereDecision = () => {
      if (isLerePricingRefresh || isHoldingOffersForLereDecision) {
        return
      }

      if (hasEligibleSurchargedSearchResultInState(getState(), filters) === true) {
        isHoldingOffersForLereDecision = true
      }
    }

    const dropHeldOffers = () => {
      isHoldingOffersForLereDecision = false
      heldOfferIds = []
      heldMapOfferIds = []
    }

    const releaseHeldOffers = () => {
      if (!isHoldingOffersForLereDecision) {
        return
      }

      isHoldingOffersForLereDecision = false
      const offerIdsToRelease = heldOfferIds
      const mapOfferIdsToRelease = heldMapOfferIds
      heldOfferIds = []
      heldMapOfferIds = []

      if ((offerIdsToRelease.length || mapOfferIdsToRelease.length) && isCurrentStream()) {
        dispatch({
          type: API_CALL_SUCCESS,
          api: FETCH_OFFER_LIST,
          key: offerListKey,
          data: {
            ...(offerIdsToRelease.length ? { streamedOfferIds: unique(offerIdsToRelease) } : {}),
            ...(mapOfferIdsToRelease.length ? { streamedMapOfferIds: unique(mapOfferIdsToRelease) } : {}),
          },
        })
      }
    }

    const cancelBufferedOffersFlush = () => {
      if (flushBufferedOffersTimeout) {
        clearTimeout(flushBufferedOffersTimeout)
        flushBufferedOffersTimeout = undefined
      }
    }

    const discardBufferedOffers = () => {
      bufferedStreamedOfferIds = []
      bufferedStreamedMapOfferIds = []
      bufferedOfferMetaData = []
    }

    const cancelAndDiscardBufferedOffers = () => {
      cancelBufferedOffersFlush()
      discardBufferedOffers()
      dropHeldOffers()
    }

    const scheduleLereSearchPricingEvaluation = (onDecided?: (result: { assignedLereGroup?: string, didAttemptDecide: boolean }) => void) => {
      const handleSettled = (result: { assignedLereGroup?: string, didAttemptDecide: boolean }) => {
        if (result.assignedLereGroup) {
          // Priced refresh re-streams with lereGroup; held unpriced offers are discarded.
          dropHeldOffers()
          onDecided?.(result)

          if (isCurrentStream()) {
            dispatch(refreshOfferListWithLereGroup(filters, listenerId, result.assignedLereGroup, minRequiredOffers))
          }
          return
        }

        if (result.didAttemptDecide) {
          releaseHeldOffers()
        }

        onDecided?.(result)
      }

      evaluateLereSearchPricingForSearchResults({
        dispatch,
        getState,
        filters,
        isLerePricingRefresh,
        onEligibleSurchargedResultFound: () => {
          isHoldingOffersForLereDecision = true
        },
      }).then(handleSettled, () => {
        // Rejected evaluations resolve as no group so held offers are not stranded.
        handleSettled({ didAttemptDecide: false })
      })
    }

    const shouldBufferOfferMetaData = (metaData: App.OfferListMetaData | undefined): metaData is App.OfferListMetaData => !!(
      metaData && offerListKeepsSearchMetadata(filters)
    )

    const flushBufferedOffers = () => {
      flushBufferedOffersTimeout = undefined

      const offerIdsToFlush = bufferedStreamedOfferIds
      const mapOfferIdsToFlush = bufferedStreamedMapOfferIds
      const metaDataToFlush = bufferedOfferMetaData

      bufferedStreamedOfferIds = []
      bufferedStreamedMapOfferIds = []
      bufferedOfferMetaData = []

      if (metaDataToFlush.length) {
        dispatch(setSearchResultMetadata(metaDataToFlush, filters, offerListKey))
        maybeStartHoldingForLereDecision()
        scheduleLereSearchPricingEvaluation()
      }

      if (isHoldingOffersForLereDecision) {
        heldOfferIds.push(...offerIdsToFlush)
        heldMapOfferIds.push(...mapOfferIdsToFlush)
      } else if (offerIdsToFlush.length || mapOfferIdsToFlush.length) {
        dispatch({
          type: API_CALL_SUCCESS,
          api: FETCH_OFFER_LIST,
          key: offerListKey,
          data: {
            ...(offerIdsToFlush.length ? { streamedOfferIds: unique(offerIdsToFlush) } : {}),
            ...(mapOfferIdsToFlush.length ? { streamedMapOfferIds: unique(mapOfferIdsToFlush) } : {}),
          },
        })
      }
    }

    const scheduleBufferedOffersFlush = () => {
      if (flushBufferedOffersTimeout) return
      flushBufferedOffersTimeout = setTimeout(flushBufferedOffers, STREAM_DISPATCH_BUFFER_MS)
    }

    const bufferStreamedOffer = (
      response: OfferResponse,
      targetOfferIds: Array<string>,
    ) => {
      if (response.searchId !== searchId) return

      const { offerId, metaData } = mapOfferListResponse(response.result, markupSource, undefined, undefined, searchRoomCount)
      targetOfferIds.push(offerId)
      if (shouldBufferOfferMetaData(metaData)) {
        bufferedOfferMetaData.push(metaData)
      }
      scheduleBufferedOffersFlush()
    }

    const handleOfferResponse = (response: OfferResponse) => {
      bufferStreamedOffer(response, bufferedStreamedOfferIds)
    }

    const handleItemResponse = (response: SearchItemResponse) => {
      if (response.searchId !== searchId) return

      if (response.results.length < 1) return
      if (response.results.length === 1) {
        handleOfferResponse({ searchId, result: response.results[0] })
        return
      }

      const [primaryOffer, secondaryOffer] = response.results
      const groupedItemType = response.itemType

      const { offerId: secondaryOfferId, metaData: secondaryOfferMetaData } = mapOfferListResponse(secondaryOffer, markupSource, undefined, groupedItemType, searchRoomCount)
      const { offerId: primaryOfferId, metaData: primaryOfferMetaData } = mapOfferListResponse(primaryOffer, markupSource, secondaryOfferId, groupedItemType, searchRoomCount)

      bufferedStreamedOfferIds.push(primaryOfferId)

      if (secondaryOfferMetaData && shouldBufferOfferMetaData(primaryOfferMetaData)) {
        bufferedOfferMetaData.push(primaryOfferMetaData, secondaryOfferMetaData)
      }
      scheduleBufferedOffersFlush()
    }

    const handleSeparatorResponse = (response: SeparatorResponse) => {
      if (response.searchId !== searchId) return

      cancelBufferedOffersFlush()
      flushBufferedOffers()

      const extra = mapSeparatorToExtra(response)

      if (extra) {
        dispatch(addOfferListExtra(offerListKey, extra))
      }
    }

    const handleSponsoredResponse = (response: SponsoredOfferResponse) => {
      if (response.searchId !== searchId) return

      cancelBufferedOffersFlush()
      flushBufferedOffers()

      const { offerId, metaData } = mapOfferListResponse(response.result, markupSource, undefined, undefined, searchRoomCount)

      if (shouldBufferOfferMetaData(metaData)) {
        dispatch(setSearchResultMetadata([metaData], filters, offerListKey))
      }

      dispatch({
        type: ADD_SPONSORED_OFFER,
        key: offerListKey,
        offerId,
        position: response.position,
      })
    }

    const handleEndResponse = (response: EndResponse) => {
      if (response.searchId !== searchId) return

      cancelBufferedOffersFlush()
      flushBufferedOffers()

      clearLastMessage(searchId)
      dispatch(endOfferListStream({ offerListKey, searchId }))
      dispatch({
        type: SET_OFFER_LIST_SOCKET_STATUS,
        key: offerListKey,
        status: 'not-listening',
      })
      removeAllSearchSocketListenersAndSafelyDisconnect({ eventGroupKey: offerListKey })

      scheduleLereSearchPricingEvaluation((result) => {
        if (isHoldingOffersForLereDecision && !result.assignedLereGroup) {
          releaseHeldOffers()
        }
      })
    }

    const handleErrorResponse = (errorResponse: ErrorResponse) => {
      // no clearLastMessage here as it will be called in handleEndResponse if needed
      let error = 'streaming error'

      if (errorResponse.type === 'searchNotFound') {
        if (searchId !== errorResponse.searchId) return
        // Search ended; release held offers instead of waiting for lereGroup.
        releaseHeldOffers()
        handleEndResponse({ searchId })
        if (isLerePricingRefresh) {
          return
        }
        error = 'searchNotFound'
      } else if (errorResponse.type === 'validationError') {
        error = 'validationError'
      } else if (errorResponse.type === 'naturalLanguageSearch') {
        error = 'naturalLanguageSearchError'
        releaseHeldOffers()
        handleEndResponse({ searchId })
      }

      if (errorResponse.type !== 'searchNotFound' && errorResponse.type !== 'naturalLanguageSearch') {
        cancelAndDiscardBufferedOffers()
      }

      dispatch({
        type: API_CALL_FAILURE,
        api: FETCH_OFFER_LIST,
        key: offerListKey,
        error,
        preserveOfferIds: shouldPreserveOfferIds,
        isLerePricingRefresh,
      })
    }

    const handleDisconnect = () => {
      cancelAndDiscardBufferedOffers()
      dispatch({
        type: SET_OFFER_LIST_ERROR,
        key: offerListKey,
        error: 'disconnected',
        socketListenersStatus: 'not-listening',
      })
    }

    const handleConnect = () => {
      dispatch({
        type: SET_OFFER_LIST_ERROR,
        key: offerListKey,
        error: undefined,
        socketListenersStatus: 'listening',
      })
    }

    const handleLocationResponse = (response: SearchLocationResponse) => {
      const { searchId: responseSearchId, bounds, centroids } = response

      if (responseSearchId === searchId) {
        dispatch({
          type: SET_OFFER_LIST_BOUNDS,
          key: offerListKey,
          bounds,
          centroids,
        })
      }
    }

    const handleOfferDescriptionResponse = (response: OfferDescriptionResponse) => {
      const { searchId: responseSearchId, offerId, description } = response

      if (responseSearchId === searchId) {
        dispatch({
          type: UPDATE_OFFER_METADATA_DESCRIPTION,
          offerListKey,
          offerId,
          description,
        })
      }
    }

    const handleOfferDescriptionLoadingResponse = (response: OfferDescriptionLoadingResponse) => {
      const { searchId: responseSearchId } = response

      if (responseSearchId === searchId) {
        dispatch({
          type: SET_OFFER_LIST_DESCRIPTION_LOADING,
          key: offerListKey,
        })
      }
    }

    const handleSelectedFiltersResponse = (response: SelectedFiltersResponse) => {
      const { searchId: responseSearchId, filters: selectedFilters } = response

      if (responseSearchId === searchId) {
        dispatch({
          type: SET_OFFER_LIST_SELECTED_FILTERS,
          key: offerListKey,
          selectedFilters,
        })
      }
    }

    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:filters', handler: handleFiltersResponse })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:offer', handler: handleOfferResponse })
    // Map-dedicated pins; event not yet in @luxuryescapes/contract-search ResponseEvents.
    addSearchSocketListener({
      eventGroupKey: offerListKey,
      event: 'search:response:map-offer' as Parameters<typeof addSearchSocketListener>[0]['event'],
      handler: (response) => bufferStreamedOffer(response as OfferResponse, bufferedStreamedMapOfferIds),
    })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:item', handler: handleItemResponse })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:separator', handler: handleSeparatorResponse })
    addSearchSocketListener({
      eventGroupKey: offerListKey,
      event: 'search:response:sponsored' as Parameters<typeof addSearchSocketListener>[0]['event'],
      handler: (response) => handleSponsoredResponse(response as SponsoredOfferResponse),
    })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:end', handler: handleEndResponse })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:location', handler: handleLocationResponse })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:error', handler: handleErrorResponse })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:offer:description:loading', handler: handleOfferDescriptionLoadingResponse })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:offer:description', handler: handleOfferDescriptionResponse })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:selected-filters', handler: handleSelectedFiltersResponse })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'disconnect', handler: handleDisconnect })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'connect', handler: handleConnect })

    const source = getSource(state)

    if (forceRestart || getState().offer.offerLists[offerListKey]?.fetching !== false) {
      streamRequest({
        region: state.geo.currentRegionCode,
        filters: streamFilters,
        canViewLuxPlusBenefits,
        isLuxPlusEnabled: luxPlusEnabled,
        clientCheckAvailability: streamClientCheckAvailability,
        noCache,
        memberId,
        domainUserId,
        email: state.auth.account.email,
        searchId,
        minRequiredOffers,
        personalise,
        include: filters.include,
        // Segment-exact match: source is a comma-joined string, so split before checking.
        // A substring check would also match names that merely contain "metasearch"
        // (e.g. metasearchPriceVerifier), wrongly flipping publicRates on.
        publicRates: source.split(',').includes(SOURCE_TYPES.METASEARCH),
        typeaheadId,
        functionScoreWeights,
        lang: state.geo.locale,
      })
    }
  }
}

/**
 * Streams additional hotel results into an existing metasearch list. The
 * metasearch feed remains authoritative for the initial (including pinned and
 * forced-ranking) IDs; this action only ever appends unique stream results.
 */
function endMetaSearchOfferStream(dispatch: AppDispatch, offerListKey: string, searchId: string) {
  clearLastMessage(searchId)
  dispatch(endOfferListStream({ offerListKey, searchId }))
  removeAllSearchSocketListenersAndSafelyDisconnect({ eventGroupKey: offerListKey })
  dispatch({ type: SET_OFFER_LIST_SOCKET_STATUS, key: offerListKey, status: 'not-listening' })
}

export function streamAdditionalMetaSearchOffers(
  filters: App.OfferListFilters,
  offerListKey: string,
  listenerId: string,
  options: {
    excludeOfferIds: Array<string>
    minRequiredOffers: number
    /**
     * Filters to restart with when the stream ends without a single candidate,
     * e.g. when svc-search can't anchor a property search because the property
     * isn't indexed.
     */
    fallbackFilters?: App.OfferListFilters
  },
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const existingOfferList = state.offer.offerLists[offerListKey]
    if (!existingOfferList) return

    if (existingOfferList.socketListenersStatus === 'listening' && existingOfferList.searchId) {
      const currentStreamState = existingOfferList.metaSearchStream
      if (currentStreamState) {
        if (currentStreamState.receivedCandidates < currentStreamState.requestedCandidates) return

        const nextStreamState: App.MetaSearchStreamState = {
          requestedCandidates: Math.max(
            currentStreamState.requestedCandidates,
            currentStreamState.receivedCandidates,
          ) + options.minRequiredOffers,
          receivedCandidates: currentStreamState.receivedCandidates,
          requestedOfferCount: existingOfferList.offerIds.length + options.minRequiredOffers,
        }
        dispatch(setMetaSearchStreamState(offerListKey, nextStreamState))
        emitSocketMessage(
          existingOfferList.searchId,
          'search:request:extend-to',
          {
            searchId: existingOfferList.searchId,
            minOffers: nextStreamState.requestedCandidates,
          },
        )
        return
      }

      // Redux says the socket is live but its continuation state is gone. Tear
      // down the stale listeners and start a fresh stream instead of no-oping.
      clearLastMessage(existingOfferList.searchId)
      emitSocketMessage(existingOfferList.searchId, 'search:request:pause', { searchId: existingOfferList.searchId })
      removeAllSearchSocketListenersAndSafelyDisconnect({ eventGroupKey: offerListKey })
      dispatch({ type: SET_OFFER_LIST_SOCKET_STATUS, key: offerListKey, status: 'not-listening' })
    }

    const searchId = uuidV4()
    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)
    const luxPlusEnabled = getIsLuxPlusEnabled(state)
    const initialStreamState: App.MetaSearchStreamState = {
      requestedCandidates: options.minRequiredOffers,
      receivedCandidates: 0,
      requestedOfferCount: existingOfferList.offerIds.length + options.minRequiredOffers,
    }

    // Establish a stream identity without dispatching a request action: that
    // reducer path clears IDs and would briefly replace SSR-rendered results.
    dispatch({
      type: API_CALL_SUCCESS,
      api: FETCH_OFFER_LIST,
      key: offerListKey,
      searchId,
      data: { streamedOfferIds: [] },
    })
    addStateListener({ eventGroupKey: offerListKey, listenerId })
    dispatch({ type: SET_OFFER_LIST_SOCKET_STATUS, key: offerListKey, status: 'listening' })
    dispatch(setMetaSearchStreamState(offerListKey, initialStreamState))

    const handleOfferResponse = (response: OfferResponse) => {
      if (response.searchId !== searchId || !response.result?.id) return
      const lowestPrice = 'lowestPrice' in response.result ? response.result.lowestPrice : undefined
      const offerId = response.result.id
      const currentOfferList = getState().offer.offerLists[offerListKey]
      const currentStreamState = currentOfferList?.metaSearchStream
      if (!currentStreamState) return

      const isExcludedOffer = options.excludeOfferIds.includes(offerId) || currentOfferList.offerIds.includes(offerId)
      const isUsableOffer = typeof lowestPrice === 'number' && lowestPrice > 0 && !isExcludedOffer

      // The server can overshoot its minimum; keep useful results even after
      // the requested batch is full rather than permanently skipping them.
      if (isUsableOffer) {
        dispatch({
          type: API_CALL_SUCCESS,
          api: FETCH_OFFER_LIST,
          key: offerListKey,
          data: { streamedOfferId: offerId },
        })
      }
      const receivedCandidates = currentStreamState.receivedCandidates + 1
      const missingOffers = currentStreamState.requestedOfferCount -
        (currentOfferList.offerIds.length + (isUsableOffer ? 1 : 0))
      const shouldExtendBatch = receivedCandidates >= currentStreamState.requestedCandidates && missingOffers > 0
      const nextStreamState: App.MetaSearchStreamState = {
        ...currentStreamState,
        receivedCandidates,
        requestedCandidates: shouldExtendBatch ? receivedCandidates + missingOffers : currentStreamState.requestedCandidates,
      }
      dispatch(setMetaSearchStreamState(offerListKey, nextStreamState))

      // Candidate progress must stay aligned with the server cursor, but a
      // duplicate or unpriced candidate must not satisfy the visible batch.
      if (shouldExtendBatch) {
        emitSocketMessage(searchId, 'search:request:extend-to', {
          searchId,
          minOffers: nextStreamState.requestedCandidates,
        })
      }
    }

    const stop = () => {
      endMetaSearchOfferStream(dispatch, offerListKey, searchId)
    }

    const handleEndResponse = (response: EndResponse) => {
      if (response.searchId !== searchId) return

      const receivedCandidates = getState().offer.offerLists[offerListKey]?.metaSearchStream?.receivedCandidates ?? 0
      if (receivedCandidates === 0 && options.fallbackFilters) {
        // Release this search without ending the list, then restart with the
        // fallback filters so the empty search doesn't hide Load more.
        clearLastMessage(searchId)
        removeAllSearchSocketListenersAndSafelyDisconnect({ eventGroupKey: offerListKey })
        dispatch({ type: SET_OFFER_LIST_SOCKET_STATUS, key: offerListKey, status: 'not-listening' })
        dispatch(streamAdditionalMetaSearchOffers(options.fallbackFilters, offerListKey, listenerId, {
          excludeOfferIds: options.excludeOfferIds,
          minRequiredOffers: options.minRequiredOffers,
        }))
        return
      }

      stop()
    }

    const handleErrorResponse = (response: ErrorResponse) => {
      // A stream error is deliberately non-destructive: meta-offer IDs remain
      // visible and the normal metasearch error/empty-state behaviour is kept.
      if (response.type !== 'validationError' && response.searchId !== searchId) return
      stop()
    }

    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:offer', handler: handleOfferResponse })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:end', handler: handleEndResponse })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:error', handler: handleErrorResponse })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'disconnect', handler: stop })

    streamRequest({
      region: state.geo.currentRegionCode,
      filters,
      canViewLuxPlusBenefits,
      isLuxPlusEnabled: luxPlusEnabled,
      memberId: state.auth.account.memberId,
      domainUserId: getDomainUserId(),
      email: state.auth.account.email,
      searchId,
      minRequiredOffers: options.minRequiredOffers,
      include: filters.include,
      distanceLte: filters.distanceLte,
      publicRates: getSource(state)?.split(',').includes(SOURCE_TYPES.METASEARCH) ?? false,
      lang: state.geo.locale,
    })
  }
}

export function refreshOfferListWithLereGroup(
  filters: App.OfferListFilters,
  listenerId: string,
  lereGroupOverride?: string,
  minRequiredOffers?: number,
  options: Options = {},
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const lereGroup = lereGroupOverride ?? getLereDynamicPricingGroup(state)

    if (!lereGroup || !filters.checkIn || !filters.checkOut) {
      return
    }

    const offerListKey = getOfferListKeyForUser(filters, getCanViewLuxPlusBenefits(state))
    const offerList = state.offer.offerLists[offerListKey]

    if (!offerList) {
      return
    }

    // Offers held for the LERE decision never reached the list, so there is nothing to
    // reprice in place. Restart it as a fresh grouped search instead.
    if (!offerList.offerIds.length) {
      dispatch(streamOfferList(filters, listenerId, minRequiredOffers, undefined, {
        ...options,
        forceRestart: true,
        lereGroupOverride: lereGroup,
        noCache: true,
      }))
      return
    }

    dispatch(streamOfferList(
      filters,
      listenerId,
      offerList.offerIds.length || undefined,
      undefined,
      {
        ...options,
        forceRestart: true,
        preserveOfferIds: true,
        isLerePricingRefresh: true,
        lereGroupOverride: lereGroup,
        noCache: true,
      },
    ))
  }
}

function mapOfferListResponse(
  response: SearchResultEntry,
  markupSource?: ChannelMarkupSource,
  secondaryOfferId?: string,
  groupedItemType?: App.OfferListGroupedItemType,
  roomCount: number = 1,
): { offerId: string, metaData?: App.OfferListMetaData } {
  return {
    offerId: response.id,
    metaData: mapSearchResultToOfferListMetaData(response, 0, markupSource, secondaryOfferId, groupedItemType, roomCount),
  }
}

export function mapSeparatorToExtra(
  response: SeparatorResponse,
): App.OfferListExtra | undefined {
  let type: App.OfferListExtraType

  switch (response.type) {
    case SEPARATOR_TYPE.NEARBY_OFFERS:
      type = 'nearby'
      break
    case SEPARATOR_TYPE.SECOND_TIER_AVAILABLE:
      type = 'second-tier-available'
      break
    default:
      return
  }

  return {
    type,
    position: response.position,
  }
}

function mapOfferListFiltersResponse(response: FilterResponse): {
  filters: App.OfferListAvailableFilters
  filterOrder: App.OfferListFilterOrders
  offerCount: number
} {
  const v2 = response.filtersV2 as searchDefinitions['hotelListOffersFiltersV2'] & {
    propertyFacilities?: Array<App.FilterItem>
    roomAmenities?: Array<App.FilterItem>
    bedTypes?: Array<App.FilterItem>
  }
  return {
    filters: {
      amenities: v2.amenities,
      propertyFacilities: v2.propertyFacilities ?? [],
      roomAmenities: v2.roomAmenities ?? [],
      bedTypes: v2.bedTypes ?? [],
      bedrooms: v2.bedrooms,
      campaigns: v2.campaigns,
      customerRatings: v2.customerRatings,
      holidayTypes: v2.holidayTypes,
      inclusions: v2.inclusions,
      propertyTypes: v2.propertyTypes ?? [],
      offerTypes: v2.type,
      luxPlusFeatures: v2.luxPlusExclusive ?? [],
      loyalty: v2.societe ?? [],
      agentHubFeatures: v2.agentHubExclusive ?? [],
      paymentOptions: (v2.paymentOptions ?? []).map(mapPaymentOptionLabel),
      chains: v2.chains ?? [],
      total: response.total,
    },
    filterOrder: response.filterOrderV2 as App.OfferListFilterOrders,
    offerCount: response.total,
  }
}

function getOfferListFilters(results: {
  filters: App.OfferListAvailableFilters
  filterOrder: App.OfferListFilterOrders
  offerCount: number
}): Omit<App.OfferListFilterOptions, 'error' | 'fetching' | 'searchId'> {
  const filters = results.filters
  const filterOrder = results.filterOrder ?? { amenities: {}, holidayTypes: {}, propertyTypes: {} }
  return {
    filters,
    filterOrder,
    orderedFilters: {
      holidayTypes: sortBy(
        filters.holidayTypes,
        (item) => item.count,
        'desc',
      ).map((item) => ({
        value: item.slug,
        label: item.label,
        count: item.count,
      })),
      amenities: sortBy(
        filters.amenities,
        (item) => item.count,
        'desc',
      ).map((item) => ({
        value: item.slug,
        label: item.label,
        count: item.count,
      })),
    },
  }
}

export function removeOffer(offerId: string): AppAction {
  return {
    type: REMOVE_OFFER,
    offerId,
  }
}

export function fetchCalculatedSurchargeMargin(
  roomRateId: string,
  surchargeMarginItem: App.ToBeCalculatedSurchargeMarginItem,
  surchargeMarginKey: string,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const currentSurchargeMargin = state.offer.surchargeMargins[surchargeMarginKey]

    if (!currentSurchargeMargin) {
      dispatch({
        type: API_CALL,
        api: FETCH_SURCHARGE_MARGIN,
        surchargeMarginKey,
        request: () => batchFetchSurchargeMargin([roomRateId, surchargeMarginItem]),
      })
    }
  }
}

export function fetchLereDynamicPricingSurchargeTokens(): AppAction {
  return (dispatch) => {
    if (lereSurchargeTokenBatchTimer) {
      clearTimeout(lereSurchargeTokenBatchTimer)
    }

    lereSurchargeTokenBatchTimer = setTimeout(() => {
      lereSurchargeTokenBatchTimer = undefined
      lereSurchargeTokenBatchRetryCount = 0
      dispatch(fetchLereDynamicPricingSurchargeTokenBatch())
    }, LERE_SURCHARGE_TOKEN_BATCH_DELAY_MS)
  }
}

let lereSurchargeTokenBatchTimer: ReturnType<typeof setTimeout> | undefined
let lereSurchargeTokenBatchRetryTimer: ReturnType<typeof setTimeout> | undefined
let lereSurchargeTokenBatchRetryCount = 0
const LERE_SURCHARGE_TOKEN_BATCH_DELAY_MS = 50
const LERE_SURCHARGE_TOKEN_BATCH_RETRY_DELAY_MS = 200
const LERE_SURCHARGE_TOKEN_BATCH_MAX_RETRIES = 30

function scheduleLereSurchargeTokenBatchRetry(
  dispatch: AppDispatch,
  reason: string,
  details: Record<string, unknown>,
): void {
  if (lereSurchargeTokenBatchRetryCount >= LERE_SURCHARGE_TOKEN_BATCH_MAX_RETRIES) {
    console.warn('[LERE surcharge] batch retry exhausted', { reason, ...details, lereSurchargeTokenBatchRetryCount })
    return
  }

  if (lereSurchargeTokenBatchRetryTimer) {
    return
  }

  lereSurchargeTokenBatchRetryCount += 1
  lereSurchargeTokenBatchRetryTimer = setTimeout(() => {
    lereSurchargeTokenBatchRetryTimer = undefined
    dispatch(fetchLereDynamicPricingSurchargeTokenBatch())
  }, LERE_SURCHARGE_TOKEN_BATCH_RETRY_DELAY_MS)
}

function fetchLereDynamicPricingSurchargeTokenBatch(): AppAction {
  return async(dispatch, getState): Promise<void> => {
    const state = getState()
    const lereGroup = getLereDynamicPricingGroup(state)
    const cartItems = getLeHotelItems(state)
    const hotelOfferViews = getHotelItemViews(state).data
    const offers = state.offer.offers
    const postPurchase = findPostPurchaseCheckout(state.checkout.cart.mode)
    const expectedPayloads = buildLereSurchargeTokenPayloadsFromCartItems(cartItems, offers, lereGroup)
    const payloads = buildLereDynamicPricingSurchargeTokenPayloads(hotelOfferViews, lereGroup, postPurchase)

    if (!lereGroup) {
      scheduleLereSurchargeTokenBatchRetry(dispatch, 'missing_lere_group', { expectedPayloadCount: expectedPayloads?.length ?? 0, builtPayloadCount: payloads.length })
      return
    }

    if (expectedPayloads === undefined) {
      scheduleLereSurchargeTokenBatchRetry(dispatch, 'offers_loading', { builtPayloadCount: payloads.length })
      return
    }

    if (expectedPayloads.length === 0) {
      lereSurchargeTokenBatchRetryCount = 0
      return
    }

    if (payloads.length < expectedPayloads.length) {
      scheduleLereSurchargeTokenBatchRetry(dispatch, 'payloads_incomplete', {
        expectedPayloadCount: expectedPayloads.length,
        builtPayloadCount: payloads.length,
        payloads,
      })
      return
    }

    const pendingPayloads = payloads.filter((payload) => {
      const lereSurchargeTokenKey = createLereSurchargeTokenKey(payload)
      return !state.offer.lereSurchargeTokens[lereSurchargeTokenKey]
    })

    if (pendingPayloads.length === 0) {
      lereSurchargeTokenBatchRetryCount = 0
      return
    }

    lereSurchargeTokenBatchRetryCount = 0

    for (const payload of pendingPayloads) {
      dispatch({
        type: API_CALL_REQUEST,
        api: FETCH_LERE_SURCHARGE_TOKEN,
        lereSurchargeTokenKey: createLereSurchargeTokenKey(payload),
      })
    }

    const currency = getCheckoutCurrency(state) || state.geo.currentCurrency
    const distinctId = getCachedLereLabDistinctId(state)

    try {
      const lereSurchargeTokens = await createLereSurchargeTokens(
        pendingPayloads,
        currency,
        distinctId,
        { accessToken: state.auth.accessToken },
      )

      for (const [lereSurchargeTokenKey, lereSurchargeToken] of Object.entries(lereSurchargeTokens)) {
        if (lereSurchargeToken.status === 'success') {
          dispatch({
            type: API_CALL_SUCCESS,
            api: FETCH_LERE_SURCHARGE_TOKEN,
            lereSurchargeTokenKey,
            data: lereSurchargeToken,
          })
        } else {
          dispatch({
            type: API_CALL_FAILURE,
            api: FETCH_LERE_SURCHARGE_TOKEN,
            lereSurchargeTokenKey,
            error: new Error('LERE surcharge token unavailable'),
          })
        }
      }
    } catch(error) {
      for (const payload of pendingPayloads) {
        dispatch({
          type: API_CALL_FAILURE,
          api: FETCH_LERE_SURCHARGE_TOKEN,
          lereSurchargeTokenKey: createLereSurchargeTokenKey(payload),
          error,
        })
      }
    }
  }
}

export function fetchPropertyOfferMap(
  offerType: typeof OFFER_TYPE_ALWAYS_ON | typeof OFFER_TYPE_BED_BANK,
  offerId: string,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    // We don't expect Salesforce offer IDs and bedbank service property IDs to conflict.
    if (state.offer.propertyOfferMappings[offerId]) {
      // We've already fetched this record.
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_PROPERTY_OFFER_MAPPING,
      request: () => OfferService.getPropertyOfferMapping(offerType, offerId),
      offerId,
    })
  }
}

export function updateOfferListKey({ offerListKey, listenerId }: { offerListKey: string, listenerId: string }): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const offerList = state.offer.offerLists[offerListKey]
    removeStateListenerAndMaybePause({
      eventGroupKey: offerListKey,
      listenerId,
      searchId: offerList?.searchId,
      streamEnded: !!offerList?.streamEnded,
    })

    const offerListStateListeners = getStateListeners(offerListKey)

    if (offerListStateListeners.length < 1) {
      dispatch({
        type: SET_OFFER_LIST_SOCKET_STATUS,
        key: offerListKey,
        status: 'not-listening',
      })
    }
  }
}

export function getMinOffers({ offerListKey, minOffers }: { offerListKey: string, minOffers: number }): AppAction {
  return (_, getState) => {
    const state = getState()
    const offerList = state.offer.offerLists[offerListKey]
    const offerListFilters = state.offer.offerListFilterOptions[offerListKey]

    if (offerList?.socketListenersStatus === 'listening' &&
      offerListFilters &&
      !offerList?.streamEnded &&
      offerList?.searchId &&
      !offerList?.fetching &&
      offerList?.offerIds.length < minOffers
    ) {
      emitSocketMessage(
        offerList.searchId,
        'search:request:extend-to',
        { searchId: offerList.searchId, minOffers },
      )
    }
  }
}

interface FetchMetaSearchOfferParams {
  propertyId: string
  filters: App.OfferListFilters
  source: string
  utmBp?: string
  regionCode: string
  offerListKey: string
  alternativeSourceOfferListKey: string
}

export function fetchMetaSearchOfferRates(params: FetchMetaSearchOfferParams): AppAction {
  const { propertyId, filters, source, utmBp, regionCode, offerListKey, alternativeSourceOfferListKey } = params
  return (dispatch, getState) => {
    const state: App.State = getState()

    // Determine alternative source to check for caching
    let alternativeSource = source === 'metasearch' ? undefined : 'metasearch'
    if (checkBypassMode(utmBp) && !alternativeSource) {
      alternativeSource = 'loggedInMetasearch'
    }

    // Check if we already have the required metadata entries - if so, skip fetching
    const existingAlternativeMetaData = alternativeSource
      ? state.offer.searchResultMetadata.offerMetaData[alternativeSourceOfferListKey]?.[propertyId]
      : undefined
    const existingOriginalMetaData = state.offer.searchResultMetadata.offerMetaData[offerListKey]?.[propertyId]

    // If we have both metadata entries (or just original when no alternative source), no need to fetch
    const hasAlternativeData = alternativeSource ? !!existingAlternativeMetaData : true
    const hasOriginalData = !!existingOriginalMetaData

    if (hasAlternativeData && hasOriginalData) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER_META_DATA,
      offerId: propertyId,
      alternativeOfferMetaDataKey: alternativeSourceOfferListKey,
      originalOfferMetaDataKey: offerListKey,
      request: async() => {
        const latestState = getState()
        const offer = latestState.offer.offers[propertyId]
        const bedbankOffer = latestState.offer.bedbankOffers[propertyId] ?? latestState.offer.bedbankOfferSummaries[propertyId]
        const priced = ghaPricedRequest(source, getGhaPriceMatchForProperty(latestState, propertyId))

        const originalSourceBedbankRates = (filters.checkIn && filters.checkOut)
          ? await getRatesForBedbankOffer({
              id: propertyId,
              rooms: filters.rooms ?? [{ adults: 2 }],
              checkIn: filters.checkIn,
              checkOut: filters.checkOut,
              region: regionCode,
              isSpoofed: false,
              source: priced.source,
              gha: priced.gha,
              timezone: offer?.property?.timezone,
              markupSource: getChannelMarkupSource(latestState),
              channelMarkupLeadSell: bedbankOffer && getBedbankOfferChannelMarkupLeadSell(bedbankOffer),
            })
          : []

        const originalRates = originalSourceBedbankRates
          ? sortBy(
              originalSourceBedbankRates.filter(rate => !rate.isFlightBundle),
              selection => selection.totals.total,
              'asc',
            )
          : undefined
        const originalRate = originalRates?.[0]

        const createOfferMetaData = (rate: App.BedbankRate) => {
          return {
            kind: 'bedbank',
            offerId: propertyId,
            propertyId: propertyId,
            type: 'bedbank_hotel',
            distance: 0,
            available: true,
            listPosition: 0,
            rooms: [],
            packagesWithAvailableUpgrade: [],
            suggestedTravelDates: {
              checkIn: filters.checkIn ?? '',
              checkOut: filters.checkOut ?? '',
              memberPrice: rate.totals.memberTotal,
            },
            pricing: {
              lowestPrice: rate.totals.total,
              lowestMemberPrice: rate.totals.memberTotal,
              propertyFees: rate.totals.propertyFees,
              lowestPriceValue: (rate.totals.total ?? 0) - (rate.totals.propertyFees ?? 0),
              lowestPriceRoomRateId: rate.id,
              lowestPriceRoomRateGroupId: rate.groupId,
              lowestPriceMobilePromotion: rate.mobilePromotion,
              duration: rate.nights,
              hasLuxPlusRate: false,
              mx: rate.mx,
              calculationMethod: rate.calculationMethod,
            },
            cancellationPolicyInfo: {
              refundable: rate.refundable,
              partiallyRefundable: rate.partiallyRefundable,
              nonRefundableDateRanges: [],
              cancellationPolicies: rate.cancellationPolicy?.policies?.map(policy => ({
                currency: policy.currency,
                start: policy.start,
                end: policy.end,
                percent: policy.percent,
                amount: policy.amount,
                nights: policy.nights,
              })) ?? [],
            },
            availableRooms: 1,
          }
        }
        const originalOfferMetaData = originalRate
          ? createOfferMetaData(originalRate)
          : undefined

        // Include bedbankRatesData in the return value (not as a side-effect dispatch)
        // so it survives the serverStateCache and is available on every SSR request,
        // including cache hits where the request() function is never called.
        const bedbankRatesData = (!getShouldUseSearchPrices(filters) && filters.checkIn && filters.checkOut)
          ? {
              rates: originalSourceBedbankRates,
              filterKey: `${filters.checkIn}-${filters.checkOut}-${serialiseOccupancy(filters.rooms ?? [{ adults: 2 }]).join(',')}`,
            }
          : undefined

        return {
          alternativeOfferMetaData: undefined,
          originalOfferMetaData,
          bedbankRatesData,
        }
      },
    })
  }
}

interface FetchBedbankOfferWithRatesParams {
  propertyId: string
  filters: App.OfferListFilters
  source: string
  regionCode: string
  offerListKey: string
}

/**
 * Fetches one bedbank offer and its rates in a single API call.
 * The success payload updates both offer and rates state.
 */
export function fetchBedbankOfferWithRates(params: FetchBedbankOfferWithRatesParams): AppAction {
  const { propertyId, filters, source, regionCode, offerListKey } = params
  return (dispatch, getState) => {
    const state: App.State = getState()

    if (state.offer.offers[propertyId] || state.offer.bedbankOffers[propertyId]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_BEDBANK_OFFER_WITH_RATES,
      offerId: propertyId,
      offerMetaDataKey: offerListKey,
      request: async() => {
        const priced = ghaPricedRequest(source, getGhaPriceMatchForProperty(state, propertyId))
        const { offer, rates } = await getBedbankOfferWithRates(propertyId, {
          checkIn: filters.checkIn,
          checkOut: filters.checkOut,
          region: regionCode,
          rooms: filters.rooms ?? [{ adults: 2 }],
          isSpoofed: false,
          source: priced.source,
          gha: priced.gha,
          markupSource: getChannelMarkupSource(state),
        })

        const filteredRates = rates.filter((rate) => !rate.isFlightBundle)
        const sortedRates = sortBy(filteredRates, (rate) => rate.totals.total, 'asc')
        const cheapestRate = sortedRates[0]

        const offerMetaData: App.OfferListMetaData | undefined = cheapestRate
          ? {
              kind: 'bedbank',
              offerId: propertyId,
              type: 'bedbank_hotel',
              distance: 0,
              available: true,
              listPosition: 0,
              rooms: [],
              packagesWithAvailableUpgrade: [],
              suggestedTravelDates: {
                checkIn: filters.checkIn ?? '',
                checkOut: filters.checkOut ?? '',
                memberPrice: cheapestRate.totals.memberTotal,
              },
              pricing: {
                lowestPrice: cheapestRate.totals.total,
                lowestMemberPrice: cheapestRate.totals.memberTotal,
                propertyFees: cheapestRate.totals.propertyFees,
                lowestPriceValue: (cheapestRate.totals.total ?? 0) - (cheapestRate.totals.propertyFees ?? 0),
                lowestPriceRoomRateId: cheapestRate.id,
                lowestPriceRoomRateGroupId: cheapestRate.groupId,
                lowestPriceMobilePromotion: cheapestRate.mobilePromotion,
                duration: cheapestRate.nights,
                hasLuxPlusRate: false,
                calculationMethod: cheapestRate.calculationMethod,
                mx: cheapestRate.mx,
              },
              cancellationPolicyInfo: {
                refundable: cheapestRate.refundable,
                partiallyRefundable: cheapestRate.partiallyRefundable,
                nonRefundableDateRanges: [],
                cancellationPolicies: cheapestRate.cancellationPolicy?.policies?.map((policy) => ({
                  currency: policy.currency,
                  start: policy.start,
                  end: policy.end,
                  percent: policy.percent,
                  amount: policy.amount,
                  nights: policy.nights,
                })) ?? [],
              },
              availableRooms: 1,
            }
          : undefined

        // Keep the same bedbankRatesData shape expected by existing reducers/selectors.
        const bedbankRatesData = (!getShouldUseSearchPrices(filters) && filters.checkIn && filters.checkOut)
          ? {
              rates,
              filterKey: `${filters.checkIn}-${filters.checkOut}-${serialiseOccupancy(filters.rooms ?? [{ adults: 2 }]).join(',')}`,
            }
          : undefined

        return {
          offer,
          offerMetaData,
          bedbankRatesData,
        }
      },
    })
  }
}

export function fetchPropertyTags(propertyId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.offer.propertyTags[propertyId]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_PROPERTY_TAGS,
      propertyId,
      request: () => SearchService.getSearchTags({ propertyId }),
    })
  }
}

export function fetchPropertyNearby(propertyId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.offer.propertyNearby[propertyId]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_PROPERTY_NEARBY,
      propertyId,
      request: () => SearchService.getPropertyNearby(propertyId),
    })
  }
}

interface FetchOfferPropertySurroundingsOptions {
  region: string
  categories?: Array<App.OfferPropertySurroundingsCategory>
  limit?: number
  excludeTypes?: Array<string>
  englishOnly?: boolean
}

export function buildOfferPropertySurroundingsCacheKey(
  offerId: string,
  options: FetchOfferPropertySurroundingsOptions,
): string {
  return [
    offerId,
    options.region,
    options.categories?.join(',') ?? '',
    options.limit ?? '',
    options.excludeTypes?.join(',') ?? '',
    options.englishOnly ? 'en' : '',
  ].join('|')
}

export function fetchOfferPropertySurroundings(
  offerId: string,
  options: FetchOfferPropertySurroundingsOptions,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const cacheKey = buildOfferPropertySurroundingsCacheKey(offerId, options)
    const entry = state.offer.offerPropertySurroundings[cacheKey]

    if (entry?.fetching || (entry?.surroundings && !entry.error)) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER_PROPERTY_SURROUNDINGS,
      cacheKey,
      request: () => TripPlannerService.getOfferPropertySurroundings({
        offerId,
        region: options.region,
        categories: options.categories,
        limit: options.limit,
        excludeTypes: options.excludeTypes,
        englishOnly: options.englishOnly,
      }),
    })
  }
}

interface FetchOfferTagsOptions {
  travelStartDate?: string
  travelEndDate?: string
  travelDateOverlap?: boolean
  tagType?: App.OfferTagType
  includeAssignments?: boolean
  includedAssignmentEntityTypeIn?: Array<App.OfferTagAssignment['entityType']>
}

export function fetchOfferTags(offerId: string, options: FetchOfferTagsOptions = {}): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const {
      travelStartDate,
      travelEndDate,
      travelDateOverlap,
      tagType,
      includeAssignments,
      includedAssignmentEntityTypeIn,
    } = options

    const cacheKey = buildOfferTagsCacheKey(
      offerId,
      travelStartDate,
      travelEndDate,
      tagType,
      includeAssignments,
      includedAssignmentEntityTypeIn,
    )

    const tagData = state.offer.offerTags[cacheKey]
    // Skip if already fetched or currently fetching
    if (tagData) {
      return
    }

    const normalizedStartDate = travelStartDate ? moment.utc(travelStartDate).startOf('day').toISOString() : undefined
    const normalizedEndDate = travelEndDate ? moment.utc(travelEndDate).startOf('day').toISOString() : undefined

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER_TAGS,
      cacheKey,
      request: () => getTagsByOfferId({
        offerId,
        region: state.geo.currentRegionCode,
        travelStartDate: normalizedStartDate,
        travelEndDate: normalizedEndDate,
        travelDateOverlap,
        tagType,
        includeAssignments,
        includedAssignmentEntityTypeIn,
      }).then(result => result.tags),
    })
  }
}

export type MetaSearchOfferType = 'forced_rankings' | 'pinned' | 'recommended'

export function fetchMetaOfferIds(
  filters: App.OfferListFilters,
  prependOfferId?: string,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const offerListKey = getOfferListKeyForUser(filters, getCanViewLuxPlusBenefits(state))

    if (state.offer.offerLists[offerListKey] || !filters.metaSearchObject || filters.metaSearchObject.disabled) {
      return
    }

    const { metaSearchObject } = filters

    const request = () => getMetaOfferIds({
      country_code: metaSearchObject.offerCountry,
      user_region: metaSearchObject.region,
      latitude: metaSearchObject.latitude,
      longitude: metaSearchObject.longitude,
      forced_rankings_limit: metaSearchObject.limit,
      pinned_offers_limit: metaSearchObject.limit,
      offer_id: metaSearchObject.offerId,
      // Marketing router forced-rankings treatment; omitted (not sent) outside an experiment.
      forced_rankings_distance: metaSearchObject.forcedRankingsDistance,
      use_mocks: config.LERE_USE_MOCKS ? 'true' : 'false',
    }).then(response => {
      type MetaOfferIdRow = (typeof response.result.offer_ids)[number] & {
        accommodation_id?: string
      }
      // Widen only the missing field until the generated contract includes it.
      const offerRows = response.result.offer_ids as Array<MetaOfferIdRow>

      const metaSearchOfferTypes: Record<string, MetaSearchOfferType> = {}
      const metaSearchOfferDates: Record<string, { checkIn?: string, checkOut?: string }> = {}
      const metaSearchAccommodationIds: Record<string, string> = {}
      const baseIds: Array<string> = []

      offerRows.forEach((offer) => {
        baseIds.push(offer.offer_id)
        metaSearchOfferTypes[offer.offer_id] = offer.type
        if (offer.check_in || offer.check_out) {
          metaSearchOfferDates[offer.offer_id] = {
            checkIn: offer.check_in,
            checkOut: offer.check_out,
          }
        }
        if (offer.accommodation_id) {
          metaSearchAccommodationIds[offer.offer_id] = offer.accommodation_id
        }
      })

      const mainOfferList = prependOfferId
        ? [prependOfferId, ...baseIds.filter(id => id !== prependOfferId)]
        : baseIds
      return {
        mainOfferList,
        offerCount: mainOfferList.length,
        // The prepended flash offer is intentionally absent from
        // `metaSearchOfferTypes`. Adding a synthetic `'pinned'` entry would
        // make `useMetaSearchOfferList`'s discount-fetch effect dispatch
        // `fetchMetaSearchOffer` for it — wrong endpoint; flash data is
        // loaded via `fetchAccumulatedOfferById`. `undefined` is the correct
        // signal to skip both the discount fetch and the discountedPackages
        // assignment.
        metaSearchOfferTypes,
        metaSearchOfferDates,
        metaSearchAccommodationIds,
      }
    })

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER_LIST,
      request,
      key: offerListKey,
    })
  }
}

interface FetchPackageRecommendationsOptions {
  offerId: string
  occupancy?: string
  checkIn?: string
  checkOut?: string
}

export function fetchPackageRecommendations({
  offerId,
  occupancy,
  checkIn,
  checkOut,
}: FetchPackageRecommendationsOptions): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const region = state.geo.currentRegionCode

    const key = getRecommendationKey({ offerId, occupancy, checkIn, checkOut })

    const existing = state.offer.packageRecommendations?.[key]
    if (existing?.data || existing?.fetching) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_PACKAGE_RECOMMENDATIONS,
      key,
      request: () => OfferService.getPackageRecommendations({
        offerId,
        region,
        occupancy,
        checkIn,
        checkOut,
      }),
    })
  }
}
